import { useState } from "react";
import { useLocation } from "react-router";

import { notFoundPath, postForm, unavailableMessage, type FieldErrors } from "../../api/client.js";
import { loginUrlAfterUnauthorized } from "../../app/session-expiry.js";
import { useGo } from "../../app/use-go.js";

/** 送った結果を画面に渡すもの。 */
export interface Send {
    readonly busy: boolean;
    /** 欄に結び付かない断り（<c>fields</c> に無い欄の断りもここへ回す）。 */
    readonly summary: readonly string[];
    readonly fieldError: (name: string) => string | undefined;
    readonly send: (body: URLSearchParams) => void;
}

/**
 * 値を画面が持つフォームを API へ送る（契約内容の変更・解約）。
 *
 * <b>次の画面と通知は API が返す</b>（<c>{ location, notice }</c>）。画面は移るだけ。
 * <c>fields</c> は断りを欄の下に出せる欄。
 */
export const useSend = (action: string, fields: readonly string[] = []): Send => {
    const go = useGo();
    // ログインが切れていたときの戻り先（今の画面）
    const { pathname, search } = useLocation();
    const here = pathname + search;
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<FieldErrors>({});
    const shown = new Set(fields);

    const send = async (body: URLSearchParams): Promise<void> => {
        setBusy(true);
        setErrors({});
        try {
            const outcome = await postForm(action, body);
            switch (outcome.kind) {
                case "go": {
                    go(outcome.location, { notice: outcome.notice, detail: outcome.detail });
                    return;
                }
                case "unauthorized": {
                    // ログインが切れていた。知らせて、この画面へ戻れるようにする
                    go(loginUrlAfterUnauthorized(here, true));
                    return;
                }
                case "notFound": {
                    go(notFoundPath);
                    return;
                }
                case "errors": {
                    setErrors(outcome.errors);
                    break;
                }
            }
        } catch {
            setErrors({ "": [unavailableMessage] });
        }
        setBusy(false);
    };

    return {
        busy,
        summary: Object.entries(errors).filter(([name]) => !shown.has(name)).flatMap(([, messages]) => messages),
        fieldError: (name) => (shown.has(name) ? errors[name]?.join(" ") : undefined),
        send: (body) => { void send(body); },
    };
};
